/**
 * Upright chat — internal-mode debug panel (PLT-4475).
 *
 * Floating top-left overlay that surfaces per-message and session-cumulative
 * stats from the backend's `usage` SSE event: tokens (input / output / cache
 * write / cache read), wall-clock duration with a model-vs-tool-vs-other
 * breakdown, per-tool timings, stop reason, and an estimated USD cost.
 *
 * Visibility is double-gated:
 *   1. `useIsChatInternalMode()` — true only when the toggle is on AND the
 *      current user is a team user. See `useIsChatInternalMode` JSDoc for
 *      why we re-check team status here rather than trusting the Redux flag.
 *   2. The collapsed/expanded UI itself. The collapsed chip is a small,
 *      semi-transparent affordance; the expanded panel is opt-in.
 *
 * The panel reads from a module-level pub-sub store (`chatDebugStore` in
 * `upright-chat-shared.ts`) via `useSyncExternalStore` so it doesn't bloat
 * Redux with potentially-large per-message debug payloads.
 */
import { useCallback, useState, useSyncExternalStore } from 'react';
import { useThread } from '@assistant-ui/react';
import { useIsChatInternalMode } from '../common/generic/hooks';
import {
  clearChatUserInstructions,
  loadChatDebugModel,
  loadChatDebugPanelOpen,
  loadChatDebugReasoningEnabled,
  loadChatUserInstructions,
  MAX_CHAT_USER_INSTRUCTIONS_LENGTH,
  saveChatDebugModel,
  saveChatDebugPanelOpen,
  saveChatDebugReasoningEnabled,
  saveChatUserInstructions,
  type ChatDebugModelKey,
  type ChatDebugProviderKey,
} from '../common/generic/utilities/storage';
import { getCapabilities } from '@upright/common/chat/provider-capabilities';
import {
  getChatDebugSnapshot,
  getSelectedChatProvider,
  setSelectedChatProvider,
  stripLargeToolData,
  subscribeChatDebugStore,
  subscribeSelectedChatProvider,
  type ChatDebugMessageUsage,
  type ChatRoundProfile,
  webSearchFieldsForNextRequest,
} from './upright-chat-shared';

/**
 * Models exposed in the debug-panel selector (PLT-4507). Labels live here next
 * to the UI that renders them rather than in storage, so a friendly-name change
 * doesn't have to ripple through the persistence layer.
 *
 * This is a deliberate SUBSET of the backend's `CHAT_MODEL_OPTIONS` allowlist,
 * in the same order — not a mirror of it. Rows flagged `workbenchOnly` there
 * (currently Fable 5.1, PLT-6636) must NOT be added here: they exist so the AI
 * test workbench can benchmark them, and chat should not offer a model whose
 * cost and latency on real chat traffic nobody has measured yet. A backend row
 * missing from this list is therefore the intended state, not drift to fix —
 * check `workbenchOnly` before "syncing" the two. `ChatDebugModelKey` in
 * `utilities/storage.ts` backs that up: its union has no `'fable'` member, so
 * adding one here would not typecheck.
 */
const CHAT_MODEL_SELECTOR_OPTIONS: ReadonlyArray<{ key: ChatDebugModelKey; label: string }> = [
  { key: 'opus', label: 'Opus 5 (default)' },
  { key: 'sonnet', label: 'Sonnet 4.6' },
  { key: 'haiku', label: 'Haiku 4.5' },
];

/**
 * Providers exposed in the debug-panel selector (PLT-5035). `'anthropic'` is the
 * default US Anthropic API; `'bedrock-eu'` opts the request onto the EU
 * data-residency path (Claude on Bedrock, eu-west-1). This is the staff EU
 * testing switch: the backend honours it for team users only. Customers are
 * routed by the `USE_AI_CHAT_EU_RESIDENCY` grant on their user group, with no
 * switch of their own (PLT-7018).
 */
const CHAT_PROVIDER_SELECTOR_OPTIONS: ReadonlyArray<{ key: ChatDebugProviderKey; label: string }> = [
  { key: 'anthropic', label: 'Anthropic US (default)' },
  { key: 'bedrock-eu', label: 'EU residency (Bedrock eu-west-1)' },
];

// ---------------------------------------------------------------------------
// Formatting helpers — kept local to the panel so the rest of the chat
// module doesn't accidentally depend on these display formats.
// ---------------------------------------------------------------------------

/** Render token counts with a "k" suffix above 10k for compactness. */
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}

/** Render a wall-clock duration: "850ms", "2.4s", or "1m 12s" for long runs. */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * Format a USD cost. Sub-cent amounts use micro-cents (µ¢) for legibility
 * — at typical chat rates a single tool call can be sub-cent and rounding
 * to "$0.00" would hide a real signal.
 */
function formatCostUsd(usd: number | null): string {
  if (usd === null) return '—';
  if (usd < 0.001) return `${(usd * 1_000_000).toFixed(0)}µ¢`;
  if (usd < 0.01) return `${(usd * 100).toFixed(2)}¢`;
  return `$${usd.toFixed(usd < 1 ? 4 : 2)}`;
}

interface SessionTotals {
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  toolCount: number;
  costUsd: number | null;
}

/**
 * Collapse all per-message entries into one running session total. We do this
 * inside the component (rather than maintaining a separate cumulative slot)
 * so a `New chat` reset is automatic — clearing the entries clears the totals.
 */
function computeSessionTotals(entries: ChatDebugMessageUsage[]): SessionTotals {
  const totals: SessionTotals = {
    durationMs: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheCreationTokens: 0,
    cacheReadTokens: 0,
    toolCount: 0,
    costUsd: null,
  };
  for (const e of entries) {
    totals.durationMs += e.durationMs;
    totals.inputTokens += e.inputTokens;
    totals.outputTokens += e.outputTokens;
    totals.cacheCreationTokens += e.cacheCreationTokens;
    totals.cacheReadTokens += e.cacheReadTokens;
    totals.toolCount += e.toolsUsed.length;
    if (e.costUsd !== null) {
      totals.costUsd = (totals.costUsd ?? 0) + e.costUsd;
    }
  }
  return totals;
}

/** Sum tokens for the headline chip: input + output + caches, all in. */
function sumAllTokens(entry: ChatDebugMessageUsage): number {
  return entry.inputTokens + entry.outputTokens + entry.cacheCreationTokens + entry.cacheReadTokens;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Top-level panel. Mounted unconditionally inside the chat layout — it
 * self-gates on `useIsChatInternalMode` and renders nothing for non-team
 * users or when the toggle is off.
 */
export function UprightChatDebugPanel() {
  const visible = useIsChatInternalMode();
  // Persist collapsed/expanded state across reloads. Stored in localStorage
  // (separate key from the toggle) so the user's preferred panel state
  // survives toggling internal mode off and back on.
  const [open, setOpen] = useState<boolean>(() => loadChatDebugPanelOpen());
  // User-instructions blob (PLT-4492). Held in component state so the
  // textarea is responsive, and mirrored to localStorage on change so the
  // chat adapter (which reads localStorage on every send) sees the latest
  // value without prop-drilling. We hoist this state into the panel — rather
  // than the section component — because the chip header reads `length` to
  // decide whether to render the "instructions active" indicator, and that
  // would otherwise require either lifting state up later or a second
  // localStorage read on every render.
  const [userInstructions, setUserInstructions] = useState<string>(() => loadChatUserInstructions());
  // PLT-4507: model + reasoning toggle state for the debug panel. Same
  // pattern as `userInstructions` — held in component state so the form
  // is responsive, mirrored to localStorage on every change so the
  // chat adapter (which re-reads localStorage per send) sees the latest
  // value without prop-drilling.
  const [debugModel, setDebugModelState] = useState<ChatDebugModelKey>(() => loadChatDebugModel());
  const [reasoningEnabled, setReasoningEnabledState] = useState<boolean>(() => loadChatDebugReasoningEnabled());
  // PLT-5035 / PLT-5034: EU data-residency provider override. Sourced from the
  // shared pub-sub store (not a local `useState`) so the composer — which hides
  // affordances off the same value — and this panel stay in lockstep, and so
  // there's no redundant copy of the selection to keep in sync. The setter
  // persists to localStorage too, so the chat adapter's per-send read agrees.
  const debugProvider = useSyncExternalStore(
    subscribeSelectedChatProvider,
    getSelectedChatProvider,
    getSelectedChatProvider,
  );

  const snapshot = useSyncExternalStore(subscribeChatDebugStore, getChatDebugSnapshot, getChatDebugSnapshot);

  const togglePanel = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      saveChatDebugPanelOpen(next);
      return next;
    });
  }, []);

  const handleUserInstructionsChange = useCallback((value: string) => {
    // Hard cap at the storage limit even before persisting. The textarea also
    // sets `maxLength`, but a paste of >4000 chars is split into a typed event
    // by the browser and we want to be defensive — the backend rejects >4000.
    const capped = value.slice(0, MAX_CHAT_USER_INSTRUCTIONS_LENGTH);
    setUserInstructions(capped);
    saveChatUserInstructions(capped);
  }, []);

  const handleClearUserInstructions = useCallback(() => {
    setUserInstructions('');
    clearChatUserInstructions();
  }, []);

  const handleDebugModelChange = useCallback((next: ChatDebugModelKey) => {
    setDebugModelState(next);
    saveChatDebugModel(next);
  }, []);

  const handleReasoningEnabledChange = useCallback((next: boolean) => {
    setReasoningEnabledState(next);
    saveChatDebugReasoningEnabled(next);
  }, []);

  const handleDebugProviderChange = useCallback((next: ChatDebugProviderKey) => {
    // Writes through the shared store, which persists to localStorage AND
    // notifies subscribers (this panel + the composer) so both re-render.
    setSelectedChatProvider(next);
  }, []);

  if (!visible) return null;

  // `at(0)` returns `T | undefined` regardless of `noUncheckedIndexedAccess`,
  // so the `latest ?` checks below stay honest under the lint's type-aware
  // `no-unnecessary-condition` rule. Same runtime behaviour as `entries[0]`.
  const latest = snapshot.entries.at(0);
  const totals = computeSessionTotals(snapshot.entries);
  const hasUserInstructions = userInstructions.trim().length > 0;

  return (
    <div className={`upright-chat-debug-panel ${open ? 'upright-chat-debug-panel--open' : ''}`}>
      <button
        type="button"
        className="upright-chat-debug-panel-chip"
        onClick={togglePanel}
        aria-expanded={open}
        aria-label={open ? 'Collapse debug panel' : 'Expand debug panel'}
        title={
          hasUserInstructions
            ? 'Custom user instructions are active. Click to expand the panel.'
            : open
              ? 'Collapse debug panel'
              : 'Expand debug panel'
        }
      >
        {/*
         * Discoverability dot: rendered first so it sits at the leading edge
         * of the chip and is visible whether or not a `usage` event has been
         * received yet. We don't gate on `latest` because team users may set
         * instructions before sending the first message.
         */}
        {hasUserInstructions && (
          <span
            className="upright-chat-debug-panel-instr-indicator"
            aria-label="Custom user instructions are active"
            title="Custom user instructions are active"
          />
        )}
        {latest ? (
          <>
            <span>{formatDuration(latest.durationMs)}</span>
            <span className="upright-chat-debug-panel-sep">·</span>
            <span>{formatTokens(sumAllTokens(latest))} tok</span>
            <span className="upright-chat-debug-panel-sep">·</span>
            <span>{latest.toolsUsed.length} tools</span>
            <span className="upright-chat-debug-panel-sep">·</span>
            <span>{formatCostUsd(latest.costUsd)}</span>
          </>
        ) : (
          <span className="upright-chat-debug-panel-empty">debug · waiting for first response</span>
        )}
      </button>

      {open && (
        <div className="upright-chat-debug-panel-body">
          {latest ? <LatestMessageSection entry={latest} /> : null}
          {latest?.roundProfiles && latest.roundProfiles.length > 0 ? (
            <RoundProfilesSection rounds={latest.roundProfiles} />
          ) : null}
          <SessionTotalsSection totals={totals} messageCount={snapshot.entries.length} />
          {/* PLT-5034: the token preview calls Anthropic's US `countTokens`, so
              it ships the live conversation to a US endpoint — a residency leak
              on the EU Bedrock path (where there's no `countTokens` surface at
              all). Hide it when the active provider can't serve it; the backend
              `preview-tokens.ts` fails closed off the same `tokenPreview` flag. */}
          {getCapabilities(debugProvider).tokenPreview ? (
            <PreviewNextRequestSection userInstructions={userInstructions} />
          ) : (
            <section className="upright-chat-debug-panel-section">
              <header className="upright-chat-debug-panel-section-header">
                <span>Next request preview</span>
              </header>
              <p className="upright-chat-debug-panel-empty">
                Unavailable in EU residency mode — token counting would call a US endpoint.
              </p>
            </section>
          )}
          <ModelAndReasoningSection
            model={debugModel}
            onModelChange={handleDebugModelChange}
            reasoningEnabled={reasoningEnabled}
            onReasoningEnabledChange={handleReasoningEnabledChange}
            provider={debugProvider}
            onProviderChange={handleDebugProviderChange}
          />
          <UserInstructionsSection
            value={userInstructions}
            onChange={handleUserInstructionsChange}
            onClear={handleClearUserInstructions}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Next-request preview (PLT-4504)
// ---------------------------------------------------------------------------

/**
 * State machine for the "next request" preview. We hold the `forMessageCount`
 * the result was computed for so the UI can label a stale result without
 * silently displaying outdated numbers (a new turn arrived since refresh).
 */
type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'success';
      inputTokens: number;
      costEstimateUsd: {
        ifFullCacheMiss: number | null;
        ifFullCacheHit: number | null;
        assumingMissOnNewSuffixOnly: number | null;
      };
      forMessageCount: number;
    }
  | { status: 'error'; error: string };

/**
 * Mirror what `upright-chat-adapter` POSTs to `/api/chat`: array-shaped
 * content runs through `stripLargeToolData` (drops `suggest_followups`,
 * elides bulky code_execution image data URLs); string content is passed as-is.
 *
 * Hidden entity context (PLT-4494) is *not* prepended here. The adapter only
 * applies that on the FIRST user message, and for token-preview purposes the
 * extra ~30 characters are well inside the heuristic noise floor — keeping
 * the preview a pure read of the thread state is worth the trade-off.
 */
function serializeMessagesForPreview(
  messages: ReadonlyArray<{ role: string; content: string | readonly any[] }>,
): Array<{ role: string; content: string | readonly any[] }> {
  return messages.map(m => {
    if (Array.isArray(m.content)) {
      // `stripLargeToolData` mutates with `.map`, so hand it a fresh mutable
      // copy. The eslint-disable suppresses an `unsafe-spread` warning on the
      // intentionally-`any[]` content shape that assistant-ui exposes.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const copy: any[] = [...m.content];
      return { role: m.role, content: stripLargeToolData(copy) };
    }
    return { role: m.role, content: m.content };
  });
}

/**
 * Calls `POST /api/chat/preview-tokens` to get Anthropic's *exact* server-side
 * input-token count for the next request. Manual refresh (not auto on every
 * keystroke) because countTokens is a real API round-trip — we don't want to
 * burn an extra call on every render.
 *
 * Result is annotated with the message count it was computed against so the
 * panel can show a "(stale)" pill when the conversation has advanced since.
 */
function PreviewNextRequestSection({ userInstructions }: { userInstructions: string }) {
  // Observed message list — `useThread` re-renders this section when the
  // thread changes, which is exactly when we want the "stale" indicator to
  // light up.
  const messages = useThread(state => state.messages);
  const [state, setState] = useState<PreviewState>({ status: 'idle' });

  const refresh = useCallback(async () => {
    setState({ status: 'loading' });
    const trimmedInstructions = userInstructions.trim();
    const webSearchFields = await webSearchFieldsForNextRequest();
    try {
      // PLT-4720: cookie-based auth — the `up-access` HttpOnly cookie
      // travels via `credentials: 'include'`.
      const response = await fetch('/api/chat/preview-tokens', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: serializeMessagesForPreview(messages),
          ...webSearchFields,
          ...(trimmedInstructions.length > 0
            ? { userInstructions: trimmedInstructions.slice(0, MAX_CHAT_USER_INSTRUCTIONS_LENGTH) }
            : {}),
        }),
      });
      if (!response.ok) {
        const fallback = `${response.status} ${response.statusText}`.trim();
        let detail = fallback;
        try {
          const body = (await response.json()) as { error?: unknown };
          if (typeof body.error === 'string' && body.error.length > 0) detail = body.error;
        } catch {
          // Body wasn't JSON — keep the status-line fallback.
        }
        setState({ status: 'error', error: detail });
        return;
      }
      const data = (await response.json()) as {
        inputTokens?: unknown;
        costEstimateUsd?: {
          ifFullCacheMiss?: unknown;
          ifFullCacheHit?: unknown;
          assumingMissOnNewSuffixOnly?: unknown;
        };
      };
      if (typeof data.inputTokens !== 'number' || data.costEstimateUsd == null) {
        setState({ status: 'error', error: 'Malformed response' });
        return;
      }
      // Coerce nullable cost numbers — the backend returns `null` for unknown
      // models; treat any non-number the same way so a future server-side type
      // drift doesn't crash the panel.
      const numOrNull = (v: unknown): number | null => (typeof v === 'number' ? v : null);
      setState({
        status: 'success',
        inputTokens: data.inputTokens,
        costEstimateUsd: {
          ifFullCacheMiss: numOrNull(data.costEstimateUsd.ifFullCacheMiss),
          ifFullCacheHit: numOrNull(data.costEstimateUsd.ifFullCacheHit),
          assumingMissOnNewSuffixOnly: numOrNull(data.costEstimateUsd.assumingMissOnNewSuffixOnly),
        },
        forMessageCount: messages.length,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setState({ status: 'error', error: errorMessage });
    }
  }, [messages, userInstructions]);

  const isStale = state.status === 'success' && state.forMessageCount !== messages.length;
  const buttonLabel = state.status === 'loading' ? 'Counting…' : 'Refresh';

  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>
          Next request preview
          {isStale ? <span className="upright-chat-debug-panel-meta"> · stale</span> : null}
        </span>
        <button
          type="button"
          className="upright-chat-debug-panel-instr-clear"
          onClick={() => {
            void refresh();
          }}
          disabled={state.status === 'loading' || messages.length === 0}
          title="Calls Anthropic countTokens for the exact server-side input-token count of the NEXT /api/chat request."
        >
          {buttonLabel}
        </button>
      </header>
      {state.status === 'idle' && (
        <p className="upright-chat-debug-panel-empty">
          Click Refresh to count the exact input tokens the next /api/chat request would send.
        </p>
      )}
      {state.status === 'loading' && <p className="upright-chat-debug-panel-empty">Calling Anthropic countTokens…</p>}
      {state.status === 'error' && <p className="upright-chat-debug-panel-empty">Failed: {state.error}</p>}
      {state.status === 'success' && (
        <>
          <div className="upright-chat-debug-panel-row">
            <span className="upright-chat-debug-panel-label">Input tokens</span>
            <span className="upright-chat-debug-panel-value">{formatTokens(state.inputTokens)}</span>
          </div>
          <ul className="upright-chat-debug-panel-sublist">
            <li title="Lower bound: prefix is fully cache-warm; only cache-read rate applies.">
              <span>cache hit</span>
              <span>{formatCostUsd(state.costEstimateUsd.ifFullCacheHit)}</span>
            </li>
            <li title="Realistic mid-estimate: ~80% of input tokens come from a warm prefix; the new turn pays input rate.">
              <span>typical (warm prefix)</span>
              <span>{formatCostUsd(state.costEstimateUsd.assumingMissOnNewSuffixOnly)}</span>
            </li>
            <li title="Upper bound: prefix is cold and pays the 1h cache-write rate.">
              <span>cache miss</span>
              <span>{formatCostUsd(state.costEstimateUsd.ifFullCacheMiss)}</span>
            </li>
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Model + reasoning toggle section (PLT-4507).
 *
 * Two team-only debug knobs that travel together: which Claude model to
 * run as, and whether to enable Anthropic's `thinking` parameter. The
 * backend re-checks team membership before honouring either field, so a
 * non-team browser session that somehow forced these into a request
 * gets silently dropped server-side.
 *
 * Defaults match the pre-PLT-4507 hard-coded behaviour (Opus, reasoning
 * on) so leaving the panel alone is a no-op on the wire.
 */
function ModelAndReasoningSection({
  model,
  onModelChange,
  reasoningEnabled,
  onReasoningEnabledChange,
  provider,
  onProviderChange,
}: {
  model: ChatDebugModelKey;
  onModelChange: (next: ChatDebugModelKey) => void;
  reasoningEnabled: boolean;
  onReasoningEnabledChange: (next: boolean) => void;
  provider: ChatDebugProviderKey;
  onProviderChange: (next: ChatDebugProviderKey) => void;
}) {
  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>Model &amp; reasoning</span>
      </header>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Model</span>
        <select
          className="upright-chat-debug-panel-model-select"
          value={model}
          onChange={e => {
            // The native `<select>` is constrained to the listed
            // options, but the assertion keeps the type narrow without
            // an `as` at every callsite.
            onModelChange(e.target.value as ChatDebugModelKey);
          }}
        >
          {CHAT_MODEL_SELECTOR_OPTIONS.map(opt => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Provider</span>
        <select
          className="upright-chat-debug-panel-model-select"
          value={provider}
          onChange={e => {
            onProviderChange(e.target.value as ChatDebugProviderKey);
          }}
        >
          {CHAT_PROVIDER_SELECTOR_OPTIONS.map(opt => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="upright-chat-debug-panel-row">
        <label className="upright-chat-debug-panel-reasoning-label">
          <input
            type="checkbox"
            checked={reasoningEnabled}
            onChange={e => {
              onReasoningEnabledChange(e.target.checked);
            }}
          />
          <span>Reasoning (Anthropic adaptive thinking)</span>
        </label>
      </div>
    </section>
  );
}

/**
 * Custom user instructions section (PLT-4492).
 *
 * Free-text blob that gets appended to the chat system prompt by the backend.
 * Auto-saved to localStorage on every change; explicit "Clear" button resets
 * both state and storage in one step. The 4000-char cap is enforced both via
 * `maxLength` (browser-level) and via {@link saveChatUserInstructions} (defence
 * in depth — paste behaviour can occasionally bypass `maxLength` on older
 * browsers, and the backend would otherwise return 400).
 */
function UserInstructionsSection({
  value,
  onChange,
  onClear,
}: {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
}) {
  const length = value.length;
  const overLimit = length > MAX_CHAT_USER_INSTRUCTIONS_LENGTH;
  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>Custom user instructions</span>
        <span className="upright-chat-debug-panel-meta">
          {length}/{MAX_CHAT_USER_INSTRUCTIONS_LENGTH}
        </span>
      </header>
      <textarea
        className={`upright-chat-debug-panel-instr-textarea${
          overLimit ? ' upright-chat-debug-panel-instr-textarea--over' : ''
        }`}
        rows={6}
        value={value}
        maxLength={MAX_CHAT_USER_INSTRUCTIONS_LENGTH}
        placeholder="Free-text instructions appended to the chat system prompt for this browser only…"
        onChange={e => {
          onChange(e.target.value);
        }}
        spellCheck={false}
      />
      <div className="upright-chat-debug-panel-instr-actions">
        <button
          type="button"
          className="upright-chat-debug-panel-instr-clear"
          onClick={onClear}
          disabled={length === 0}
        >
          Clear
        </button>
      </div>
    </section>
  );
}

/**
 * "Last response" details. Token / time / cost breakdowns are rendered as
 * stacked bars so a glance shows where the message went.
 */
function LatestMessageSection({ entry }: { entry: ChatDebugMessageUsage }) {
  const totalTokens = sumAllTokens(entry);
  const tokenBuckets = [
    { label: 'input', value: entry.inputTokens, kind: 'input' },
    { label: 'output', value: entry.outputTokens, kind: 'output' },
    { label: 'cache write', value: entry.cacheCreationTokens, kind: 'cache-write' },
    { label: 'cache read', value: entry.cacheReadTokens, kind: 'cache-read' },
  ];

  const totalDuration = entry.durationMs || 1;
  const timeBuckets = [
    { label: 'model', value: entry.timeBreakdown.modelMs, kind: 'model' },
    { label: 'tools', value: entry.timeBreakdown.toolMs, kind: 'tools' },
    { label: 'other', value: entry.timeBreakdown.otherMs, kind: 'other' },
  ];

  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>Last response</span>
        <span className="upright-chat-debug-panel-meta">
          {entry.model}
          {entry.stopReason ? ` · ${entry.stopReason}` : ''}
        </span>
      </header>

      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Duration</span>
        <span className="upright-chat-debug-panel-value">{formatDuration(entry.durationMs)}</span>
      </div>
      <StackedBar buckets={timeBuckets} total={totalDuration} unit="ms" formatValue={v => formatDuration(v)} />

      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Tokens</span>
        <span className="upright-chat-debug-panel-value">{formatTokens(totalTokens)}</span>
      </div>
      <StackedBar
        buckets={tokenBuckets}
        total={Math.max(totalTokens, 1)}
        unit="tok"
        formatValue={v => formatTokens(v)}
      />

      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Cost (est.)</span>
        <span className="upright-chat-debug-panel-value">{formatCostUsd(entry.costUsd)}</span>
      </div>
      {entry.costBreakdownUsd && (
        <ul className="upright-chat-debug-panel-sublist">
          <li>
            <span>input</span>
            <span>{formatCostUsd(entry.costBreakdownUsd.input)}</span>
          </li>
          <li>
            <span>output</span>
            <span>{formatCostUsd(entry.costBreakdownUsd.output)}</span>
          </li>
          <li>
            <span>cache write</span>
            <span>{formatCostUsd(entry.costBreakdownUsd.cacheWrite)}</span>
          </li>
          <li>
            <span>cache read</span>
            <span>{formatCostUsd(entry.costBreakdownUsd.cacheRead)}</span>
          </li>
        </ul>
      )}

      {entry.toolsUsed.length > 0 && (
        <>
          <div className="upright-chat-debug-panel-row">
            <span className="upright-chat-debug-panel-label">Tools ({entry.toolsUsed.length})</span>
          </div>
          <ul className="upright-chat-debug-panel-tools">
            {entry.toolsUsed.map((t, idx) => (
              <li
                key={`${t.toolCallId}-${idx}`}
                className={t.failed ? 'upright-chat-debug-panel-tool--failed' : undefined}
              >
                <span className="upright-chat-debug-panel-tool-name">{t.name}</span>
                <span className="upright-chat-debug-panel-tool-stats">
                  {formatDuration(t.durationMs)} · ~{formatTokens(t.approxResultTokens)} tok
                  {t.failed ? ' · failed' : ''}
                </span>
                {/*
                 * Raw backend error for the internal-mode debug panel
                 * (PLT-4620 follow-up). The chat MCP scrubs the model-
                 * facing tool result so the LLM does not narrate failures
                 * as architecture; team users browsing in internal mode
                 * still need to see what actually broke. The whole panel
                 * is gated on `useIsChatInternalMode`, so this never
                 * leaks to non-team users.
                 */}
                {t.failed && t.errorMessage ? (
                  <span className="upright-chat-debug-panel-tool-error" title={t.errorMessage}>
                    {t.errorMessage}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Per-round profiling breakdown (PLT-5819). Rendered only when the backend
 * attached `roundProfiles` to the latest `usage` event (team + internal mode,
 * or the `CHAT_ROUND_PROFILE` env flag). This is the panel surface for the
 * decisive question the profiler was built to answer: within each agentic
 * round, where do the seconds go — model prefill/thinking (time to first
 * event) vs. answer generation vs. tool execution?
 *
 * Per round we show:
 *   - TTFE (time to first event): model prefill + thinking latency. A high TTFE
 *     with a low TTFC means the round spent its time thinking before emitting
 *     anything.
 *   - TTFC (time to first content): request → first user-visible text/thinking.
 *   - model / tools: the round's model wall-clock vs. tool-execution wall-clock.
 *   - tokens in/out and the tools called (empty on the final answer round).
 *
 * Honest caveat carried from the backend: Anthropic bills thinking tokens
 * inside `output_tokens`, so a round's "out" count is generation + thinking
 * combined and can't be split — a low TTFC with a high out count is the signal
 * that thinking dominated the round.
 */
function RoundProfilesSection({ rounds }: { rounds: ChatRoundProfile[] }) {
  const totalModelMs = rounds.reduce((sum, r) => sum + r.modelMs, 0);
  const totalToolMs = rounds.reduce((sum, r) => sum + r.toolMs, 0);
  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>Round profiling</span>
        <span className="upright-chat-debug-panel-meta">
          {rounds.length} round{rounds.length === 1 ? '' : 's'} · model {formatDuration(totalModelMs)} · tools{' '}
          {formatDuration(totalToolMs)}
        </span>
      </header>
      {rounds.map(r => (
        <div key={r.round} className="upright-chat-debug-panel-round">
          <div className="upright-chat-debug-panel-row">
            <span className="upright-chat-debug-panel-label">
              Round {r.round}
              {r.tools.length > 0 ? ` · ${r.tools.join(', ')}` : ' · answer'}
            </span>
            <span className="upright-chat-debug-panel-meta">
              {r.model}
              {r.reasoningEnabled ? ` · thinking${r.effort ? ` (${r.effort})` : ''}` : ''}
              {r.stopReason ? ` · ${r.stopReason}` : ''}
            </span>
          </div>
          <ul className="upright-chat-debug-panel-sublist">
            <li title="Time to first event: model prefill + thinking latency before any SDK event.">
              <span>time to first event</span>
              <span>{r.timeToFirstEventMs === null ? '—' : formatDuration(r.timeToFirstEventMs)}</span>
            </li>
            <li title="Time to first user-visible content (text or thinking summary).">
              <span>time to first content</span>
              <span>{r.timeToFirstContentMs === null ? '—' : formatDuration(r.timeToFirstContentMs)}</span>
            </li>
            <li title="Model wall-clock for the round (stream + finalMessage + intra-round retries).">
              <span>model</span>
              <span>{formatDuration(r.modelMs)}</span>
            </li>
            <li title="Wall-clock executing this round's tools (0 on the answer round).">
              <span>tools</span>
              <span>{formatDuration(r.toolMs)}</span>
            </li>
            <li title="Input / output tokens. Output combines generation AND thinking (Anthropic bills them together).">
              <span>tokens (in / out)</span>
              <span>
                {formatTokens(r.inputTokens)} / {formatTokens(r.outputTokens)}
              </span>
            </li>
          </ul>
        </div>
      ))}
    </section>
  );
}

/** Cumulative session stats — answers "how much have I spent in this thread?". */
function SessionTotalsSection({ totals, messageCount }: { totals: SessionTotals; messageCount: number }) {
  return (
    <section className="upright-chat-debug-panel-section">
      <header className="upright-chat-debug-panel-section-header">
        <span>Session totals</span>
        <span className="upright-chat-debug-panel-meta">
          {messageCount} message{messageCount === 1 ? '' : 's'}
        </span>
      </header>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Duration</span>
        <span className="upright-chat-debug-panel-value">{formatDuration(totals.durationMs)}</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Input</span>
        <span className="upright-chat-debug-panel-value">{formatTokens(totals.inputTokens)} tok</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Output</span>
        <span className="upright-chat-debug-panel-value">{formatTokens(totals.outputTokens)} tok</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Cache write</span>
        <span className="upright-chat-debug-panel-value">{formatTokens(totals.cacheCreationTokens)} tok</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Cache read</span>
        <span className="upright-chat-debug-panel-value">{formatTokens(totals.cacheReadTokens)} tok</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Cost (est.)</span>
        <span className="upright-chat-debug-panel-value">{formatCostUsd(totals.costUsd)}</span>
      </div>
      <div className="upright-chat-debug-panel-row">
        <span className="upright-chat-debug-panel-label">Tool calls</span>
        <span className="upright-chat-debug-panel-value">{totals.toolCount}</span>
      </div>
    </section>
  );
}

/**
 * Tiny stacked bar — one segment per bucket, widths proportional to value.
 * Buckets with zero value collapse to width 0% (no label) so the bar stays
 * legible even when only one bucket is non-zero.
 */
function StackedBar({
  buckets,
  total,
  unit,
  formatValue,
}: {
  buckets: Array<{ label: string; value: number; kind: string }>;
  total: number;
  unit: string;
  formatValue: (v: number) => string;
}) {
  return (
    <>
      <div className="upright-chat-debug-panel-bar">
        {buckets.map(b => {
          const pct = total > 0 ? (b.value / total) * 100 : 0;
          if (pct <= 0) return null;
          return (
            <span
              key={b.kind}
              className={`upright-chat-debug-panel-bar-seg upright-chat-debug-panel-bar-seg--${b.kind}`}
              style={{ width: `${pct}%` }}
              title={`${b.label}: ${formatValue(b.value)} ${unit}`}
            />
          );
        })}
      </div>
      <ul className="upright-chat-debug-panel-bar-legend">
        {buckets.map(b => (
          <li key={b.kind}>
            <span className={`upright-chat-debug-panel-bar-dot upright-chat-debug-panel-bar-dot--${b.kind}`} />
            <span className="upright-chat-debug-panel-bar-legend-label">{b.label}</span>
            <span className="upright-chat-debug-panel-bar-legend-value">{formatValue(b.value)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
